import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, ArrowRight, CheckCircle, Users, Building2, FileCheck, Wrench, Shield, Clock, Zap, MessageCircle, Camera, BarChart } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO, { SEOConfig } from '../components/SEO';

// Static color class maps for Tailwind compilation
const bgColorMap = {
  blue: 'bg-blue-100',
  green: 'bg-green-100',
  purple: 'bg-purple-100',
  orange: 'bg-orange-100',
  pink: 'bg-pink-100',
  red: 'bg-red-100',
  yellow: 'bg-yellow-100',
  indigo: 'bg-indigo-100',
};

const textColorMap = {
  blue: 'text-blue-600',
  green: 'text-green-600',
  purple: 'text-purple-600',
  orange: 'text-orange-600',
  pink: 'text-pink-600',
  red: 'text-red-600',
  yellow: 'text-yellow-600',
  indigo: 'text-indigo-600',
};

export default function HowItWorks() {
  const steps = [
    {
      number: "01",
      title: "Sign Up & Add Your Property",
      description: "Create your account in minutes. Add your property details, upload photos, and set your rental price. Our system automatically detects your jurisdiction (Scotland, England, or Wales) and applies the correct legal framework.",
      icon: Building2,
      color: "blue"
    },
    {
      number: "02",
      title: "We Handle Compliance",
      description: "Our Operations Centre automatically tracks all required certificates (Gas Safety, EICR, EPC) and alerts you before expiry. Order certificates directly through the platform - we assign vetted contractors and upload certificates automatically.",
      icon: Shield,
      color: "green"
    },
    {
      number: "03",
      title: "Receive Applications",
      description: "Qualified tenants apply directly. Our smart screening system analyses applications, verifies references, and provides clear recommendations. You decide who to accept - the system handles the paperwork.",
      icon: Users,
      color: "purple"
    },
    {
      number: "04",
      title: "Automated Tenancy Setup",
      description: "Once you approve a tenant, we generate jurisdiction-specific tenancy agreements, handle deposit protection, and manage the move-in process. Scotland gets PRTs, England/Wales gets ASTs - all automatic.",
      icon: FileCheck,
      color: "orange"
    },
    {
      number: "05",
      title: "Ongoing Management",
      description: "Rent collection, maintenance coordination, inspections, and tenant communications all handled through the platform. Our dedicated operations team works around the clock to keep everything running smoothly.",
      icon: Zap,
      color: "pink"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <SEO {...SEOConfig.howItWorks} />
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/for-landlords" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Landlords
            </Link>
            <Link to="/for-tenants" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Tenants
            </Link>
            <Link to="/properties" className="text-slate-600 hover:text-primary transition-colors font-medium">
              Browse Properties
            </Link>
            <Link to="/login"><Button>Sign In</Button></Link>
          </nav>
          <div className="flex md:hidden items-center gap-3">
            <Link to="/for-landlords" className="text-sm text-slate-700 font-medium">Landlords</Link>
            <Link to="/for-tenants" className="text-sm text-slate-700 font-medium">Tenants</Link>
            <Link to="/login"><Button size="sm">Sign In</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/80 text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            How LetWise Works
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            From listing to let in 5 simple steps. We have automated the entire lettings process 
            so you can focus on what matters &ndash; your investment.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          {steps.map((step, idx) => (
            <div key={idx} className="flex gap-8 mb-16 last:mb-0">
              <div className="hidden md:flex flex-col items-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${bgColorMap[step.color] || 'bg-blue-100'}`}>
                  <step.icon className={`w-8 h-8 ${textColorMap[step.color] || 'text-blue-600'}`} />
                </div>
                {idx < steps.length - 1 && (
                  <div className="w-0.5 h-full bg-slate-200 mt-4" />
                )}
              </div>
              <Card className="flex-1 p-8 hover:shadow-lg transition-shadow">
                <div className="text-sm font-bold text-primary mb-2">STEP {step.number}</div>
                <h3 className="text-2xl font-display font-bold mb-4">{step.title}</h3>
                <p className="text-slate-600 text-lg leading-relaxed">{step.description}</p>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-12">What Makes Us Different</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Multi-Jurisdiction</h3>
              <p className="text-slate-600">Automatic compliance with Scotland PRT, England AST, and Wales occupation contracts. We know the law so you do not have to.</p>
            </Card>
            <Card className="p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">24/7 Operations</h3>
              <p className="text-slate-600">Our dedicated operations team handles maintenance, communications, and emergencies round the clock.</p>
            </Card>
            <Card className="p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Full Transparency</h3>
              <p className="text-slate-600">Real-time dashboards show exactly what is happening with your property. No hidden fees, no surprises.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-display font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl opacity-90 mb-8">Join thousands of landlords who have simplified their property management.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register?role=landlord">
              <Button size="lg" className="bg-white text-primary hover:bg-slate-100 font-semibold">
                Sign Up as Landlord <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/for-tenants">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                I am a Tenant
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-display font-bold">LetWise</span>
          </div>
          <div className="grid md:grid-cols-4 gap-8 text-slate-400">
            <div>
              <h4 className="font-semibold text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/compliance" className="hover:text-white">Compliance</Link></li>
                <li><Link to="/inspections" className="hover:text-white">Inspections</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">For Users</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/for-landlords" className="hover:text-white">Landlords</Link></li>
                <li><Link to="/for-tenants" className="hover:text-white">Tenants</Link></li>
                <li><Link to="/for-agents" className="hover:text-white">Agents</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms-of-business" className="hover:text-white">Terms of Business</Link></li>
                <li><Link to="/complaints" className="hover:text-white">Complaints Procedure</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-white">Cookie Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Jurisdictions</h4>
              <ul className="space-y-2 text-sm">
                <li className="hover:text-white">Scotland (PRT)</li>
                <li className="hover:text-white">England (AST)</li>
                <li className="hover:text-white">Wales (Occupation)</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-slate-500">
            <p>© {new Date().getFullYear()} C&C LET MANAGEMENT LIMITED trading as LetWise. Company No. SC869470. PRS Member: PRS056298.</p>
            <p className="mt-1">Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, G72 7XR</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
